import { IconLoader2 } from "@tabler/icons-react";

import { IconMachine } from "../ui/icons";

import { errorMessage, useStartNodes } from "../../api/queries";
import type { Capacity } from "../../api/types";
import {
  familyLabel,
  indefiniteShapeLabel,
  isProvisioning,
  poolHasRoom,
  poolsAreFull,
  poolsBySize,
} from "../../lib/capacity";

interface StartMachineCardProps {
  capacity: Capacity;
}

/**
 * The call to action shown when no GPU is free: start a machine, by shape.
 *
 * Four honest cases: a machine is already on its way, one can be started (pick
 * the shape that fits the need), the pools have reached the ceiling the
 * console never exceeds, or Scaleway cannot be reached at all.
 *
 * Args:
 *     capacity: The measured capacity payload.
 *
 * Returns:
 *     A card explaining what to do next.
 */
export function StartMachineCard({ capacity }: StartMachineCardProps): JSX.Element {
  const startNodes = useStartNodes();
  const eta = capacity.node_start_eta_minutes;

  if (isProvisioning(capacity)) {
    return (
      <section className="cta cta--pending" aria-labelledby="start-machine-title">
        <IconLoader2 size={18} aria-hidden="true" className="spin" />
        <div>
          <h2 className="cta__title" id="start-machine-title">
            A machine is starting — about {eta} min
          </h2>
          <p className="cta__text">
            Scaleway is creating it. You can already ask for GPUs: your
            workspace will start by itself as soon as the machine is up.
          </p>
        </div>
      </section>
    );
  }

  if (poolsAreFull(capacity)) {
    return (
      <section className="cta cta--full" aria-labelledby="start-machine-title">
        <IconMachine size={18} />
        <div>
          <h2 className="cta__title" id="start-machine-title">
            No free GPU, and every machine is already running
          </h2>
          <p className="cta__text">
            The console has reached the most machines it will ever run at once.
            GPUs have to be given back before a new workspace can start — the
            list above shows who holds them.
          </p>
        </div>
      </section>
    );
  }

  if (capacity.pools.length === 0 || eta === null) {
    return (
      <section className="cta cta--full" aria-labelledby="start-machine-title">
        <IconMachine size={18} />
        <div>
          <h2 className="cta__title" id="start-machine-title">
            No free GPU, and the console cannot start a machine
          </h2>
          <p className="cta__text">
            {capacity.pools_error ??
              "The console cannot reach Scaleway, so it cannot add a machine."}{" "}
            GPUs have to be given back before a new workspace can start — the
            list above shows who holds them.
          </p>
        </div>
      </section>
    );
  }

  const startable = poolsBySize(capacity).filter(poolHasRoom);

  return (
    <section className="cta" aria-labelledby="start-machine-title">
      <IconMachine size={18} />
      <div className="cta__body">
        <h2 className="cta__title" id="start-machine-title">
          No free GPU right now
        </h2>
        <p className="cta__text">
          Start a machine in the shape you need — it takes about {eta} min and
          is stopped again when nobody uses it. Or just ask for GPUs and the
          console does this for you.
        </p>
        {startNodes.isError ? (
          <p className="cta__error" role="alert">
            {errorMessage(startNodes.error)}
          </p>
        ) : null}
        {startNodes.isSuccess ? (
          <p className="cta__ok" role="status">
            {startNodes.data.message}
          </p>
        ) : null}
      </div>
      <div className="cta__actions">
        {startable.map((pool) => (
          <button
            key={pool.pool_id}
            type="button"
            className="btn btn--primary"
            onClick={() =>
              startNodes.mutate({ gpus_per_node: pool.gpus_per_node, count: 1 })
            }
            disabled={startNodes.isPending || startNodes.isSuccess}
          >
            {startNodes.isPending
              ? "Starting…"
              : `Start ${indefiniteShapeLabel(pool.gpus_per_node)}${
                  pool.family ? ` (${familyLabel(pool.family)})` : ""
                }`}
          </button>
        ))}
      </div>
    </section>
  );
}
